import { useState, useEffect, useCallback } from 'react'
import { channelsAPI } from '../services/api'
import { getTimeRemaining } from '../utils/timeFormatters'
import { createLogger } from '../utils/logger'

const logger = createLogger('RateLimit')

/**
 * Custom hook to manage channel rate limiting
 * Tracks user's ability to post messages and provides countdown information
 * 
 * @param {string|null} channelId - The ID of the channel to check rate limits for
 * @returns {Object} Rate limit state and functions
 * @returns {Object|null} returns.rateLimitInfo - Rate limit information (can_post, posts_count, next_post_at)
 * @returns {boolean} returns.canPost - Whether user can currently post
 * @returns {string|null} returns.timeRemaining - Human-readable time until next post allowed
 * @returns {Function} returns.checkRateLimit - Function to manually refresh rate limit info
 * @returns {boolean} returns.loading - Whether rate limit check is in progress
 */
export function useChannelRateLimit(channelId) {
  const [rateLimitInfo, setRateLimitInfo] = useState(null)
  const [loading, setLoading] = useState(false)

  const checkRateLimit = useCallback(async () => {
    if (!channelId) {
      setRateLimitInfo(null)
      return
    }

    setLoading(true)
    try {
      const response = await channelsAPI.getRateLimitInfo(channelId)
      setRateLimitInfo(response.data)
    } catch (error) {
      logger.warn('Failed to check rate limit, allowing posts', { channelId, error: error.message })
      // On error, allow posting (fail open rather than fail closed)
      setRateLimitInfo(null)
    } finally {
      setLoading(false)
    }
  }, [channelId])

  // Fetch rate limit info when channel changes
  useEffect(() => {
    checkRateLimit()
  }, [checkRateLimit])

  // Calculate derived values
  // Default to true when no rate limit info (allows posting until we know there's a limit)
  const canPost = rateLimitInfo?.can_post ?? true
  
  const nextPostTime = rateLimitInfo?.next_post_at 
    ? new Date(rateLimitInfo.next_post_at).getTime()
    : null

  // Auto-refresh when rate limit expires
  useEffect(() => {
    if (!nextPostTime || canPost) {
      return // No rate limit active or already can post
    }

    const now = Date.now()
    const timeUntilExpiry = nextPostTime - now

    if (timeUntilExpiry <= 0) {
      // Already expired, refresh immediately
      logger.debug('Timer expired, refreshing immediately', { channelId, nextPostTime })
      checkRateLimit()
      return
    }

    // Set timer to refresh when rate limit expires
    logger.debug('Setting auto-refresh timer', { 
      channelId, 
      timeUntilExpiry: `${timeUntilExpiry}ms` 
    })
    const timer = setTimeout(() => {
      logger.debug('Auto-refresh timer fired', { channelId })
      checkRateLimit()
    }, timeUntilExpiry + 100) // Add 100ms buffer to ensure backend time has passed

    return () => {
      logger.debug('Cleaning up auto-refresh timer', { channelId })
      clearTimeout(timer)
    }
  }, [nextPostTime, canPost, checkRateLimit])

  return {
    rateLimitInfo,
    canPost,
    nextPostTime,
    checkRateLimit,
    isLoading: loading
  }
}
